import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/PlaylistAdd';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { exporterSeances, telecharger } from './echangeSeances.ts';
import { listerSeances } from './seancesApi.ts';
import { useLocation, useNavigate } from 'react-router-dom';

import { LegendeZones } from '../Allures/couleursZones.tsx';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { useVma } from '../Allures/useVma.ts';
import ImportSeances from './ImportSeances.tsx';
import SeancesEnregistrees from './SeancesEnregistrees.tsx';
import { MODELE_PAR_DEFAUT_MODIFIE_EVENT, useModelesParDefaut, usagesDuModele } from './composition.ts';
import { type Seance } from './seancesApi.ts';

/**
 * Page Construction : la liste des seances de l'utilisateur. Un clic sur une
 * seance, ou le bouton "Ajouter une seance", ouvre l'editeur
 * (Construction.tsx, pages /construction/:id et /construction/nouvelle).
 */
export default function MesSeances() {
  const navigate = useNavigate();
  const location = useLocation();
  const [vma] = useVma();
  const { table } = useAllureTable();
  const [rafraichissement, setRafraichissement] = React.useState(0);
  const modeles = useModelesParDefaut(rafraichissement);
  const [importOuvert, setImportOuvert] = React.useState(false);
  // Message transmis par l'editeur (ex : seance supprimee), ou local.
  const [message, setMessage] = React.useState<{ severity: 'success' | 'error'; texte: string } | null>(() => {
    const texte = (location.state as { message?: string } | null)?.message;
    return texte ? { severity: 'success', texte } : null;
  });

  // Export de toutes les seances (hors modeles) au format de l'import.
  const exporter = () =>
    listerSeances()
      .then((liste: Seance[]) => {
        const date = new Date().toISOString().slice(0, 10);
        telecharger(exporterSeances(liste), `seances-${date}.txt`);
      })
      .catch((e: Error) => setMessage({ severity: 'error', texte: 'Export impossible : ' + e.message }));

  return (
    <Box component="main" sx={{ p: 12, pt: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<DownloadIcon />} onClick={exporter}>
          Exporter
        </Button>
        <Button startIcon={<UploadIcon />} onClick={() => setImportOuvert(true)}>
          Importer
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/seances/nouvelle')}>
          Ajouter une séance
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography color="text.secondary">
          Corps de séance, séances complètes, compétitions, échauffements, récupérations et modèles. Clique sur une
          séance pour l'ouvrir.
        </Typography>
        <LegendeZones table={table} />
      </Box>

      <SeancesEnregistrees
        rafraichissement={rafraichissement}
        onOuvrir={(seance: Seance) => seance.id && navigate(`/seances/${seance.id}`)}
        onSuppression={(id: string) => {
          // Un modele par defaut supprime n'est plus la preference (cote serveur).
          if (usagesDuModele(id, modeles).length > 0) window.dispatchEvent(new Event(MODELE_PAR_DEFAUT_MODIFIE_EVENT));
        }}
        modelesParDefaut={modeles}
      />

      <ImportSeances
        ouvert={importOuvert}
        vma={vma}
        table={table}
        onFermer={() => setImportOuvert(false)}
        onImport={(nombre: number) => {
          setRafraichissement((n) => n + 1);
          setMessage({ severity: 'success', texte: `${nombre} séance(s) importée(s).` });
        }}
      />

      {message && (
        <Snackbar open autoHideDuration={6000} onClose={() => setMessage(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.texte}</Alert>
        </Snackbar>
      )}
    </Box>
  );
}
